import { workspaceOperationSchema } from '@mio/shared';

import { apiRequest } from '@/shared/api/client';

import type { WorkspaceOperation } from '@mio/shared';
import type { QueryClient } from '@tanstack/react-query';

/** The operation is still running server-side; nothing was lost. */
export class OperationStillRunningError extends Error {
  constructor(readonly operation: WorkspaceOperation) {
    super(
      'This is taking longer than usual. The operation continues in the background; check the status in a moment before trying again.',
    );
  }
}

/** A cold start or a slow provider stop can legitimately exceed several minutes. */
export const OPERATION_WAIT_MS = 5 * 60_000;

/** Shared with `useWorkspaceCommands`, so a wait and the status query poll one request. */
export const operationQueryKey = (id: string) => ['workshop', 'operation', id];

function fetchOperation(id: string, client?: QueryClient) {
  const queryFn = ({ signal }: { signal?: AbortSignal }) =>
    apiRequest(`/workshop/operations/${id}`, workspaceOperationSchema, {
      signal: signal ?? AbortSignal.timeout(15_000),
    });
  return client
    ? client.fetchQuery({ queryKey: operationQueryKey(id), queryFn, staleTime: 900 })
    : queryFn({ signal: AbortSignal.timeout(15_000) });
}

/** Do not reload an editor until the server confirms its source checkpoint. */
export async function waitForWorkspaceOperation(
  operation: WorkspaceOperation,
  options: { client?: QueryClient } = {},
) {
  const deadline = Date.now() + OPERATION_WAIT_MS;
  let failures = 0;
  while (!['succeeded', 'failed'].includes(operation.status)) {
    await new Promise<void>((resolve) => setTimeout(resolve, 1000));
    if (Date.now() >= deadline) throw new OperationStillRunningError(operation);
    try {
      operation = await fetchOperation(operation.id, options.client);
      failures = 0;
    } catch (error) {
      // One lost poll must not report a running save as a failure.
      if (++failures >= 3) throw error;
    }
  }
  return operation;
}
